// src/pages/HplPage.jsx
import { useEffect } from 'react';
import styled from 'styled-components';
import AOS from 'aos';
import 'aos/dist/aos.css';
import AreaCalculator from '../components/AreaCalculator';
import SeoMeta from '../components/SeoMeta';
import { usePageMeta } from '../hooks/usePageMeta';

const PageWrap = styled.div``;

const Hero = styled.section`
  position: relative;
  padding: 96px 0 72px;
  background:
    radial-gradient(1200px 600px at 10% -10%, rgba(255,102,0,.15), transparent 60%),
    radial-gradient(900px 500px at 100% 0%, rgba(255,102,0,.08), transparent 55%),
    ${({theme})=>theme.body};
  border-bottom: 1px solid ${({theme})=>theme.border};
`;

const Container = styled.div`
  width: ${({theme})=>theme.container};
  margin: 0 auto;
  padding: 0 0;
`;

const H1 = styled.h1`
  font-size: ${({theme})=>theme.font.sizes.h1};
  line-height: 1.1;
  margin-bottom: 12px;
`;

const Lead = styled.p`
  font-size: 18px;
  color: ${({theme})=>theme.textMuted};
  max-width: 900px;
`;

const Badges = styled.div`
  display:flex; flex-wrap:wrap; gap:8px; margin-top:18px;
  span{
    font-size:14px;
    padding:6px 10px;
    border-radius:${({theme})=>theme.radius.pill}px;
    border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.surface};
  }
`;

const Section = styled.section`
  padding: 72px 0;
  &.surface { background: ${({theme})=>theme.body}; }
`;

const Grid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(12, 1fr);

  &.two   > * { grid-column: span 6; }
  &.three > * { grid-column: span 4; }
  &.four  > * { grid-column: span 3; }

  @media (max-width: 1100px) {
    &.four  > * { grid-column: span 4; }
  }
  @media (max-width: 780px) {
    &.two > * { grid-column: span 12; }
    &.three > * { grid-column: span 6; }
    &.four > * { grid-column: span 6; }
  }
  @media (max-width: 520px) {
    &.three > * { grid-column: span 12; }
    &.four  > * { grid-column: span 12; }
  }
`;

const Card = styled.article`
  background: ${({theme})=>theme.surface};
  border: 1px solid ${({theme})=>theme.border};
  border-radius: ${({theme})=>theme.radius.lg}px;
  box-shadow: ${({theme})=>theme.shadow.sm};
  padding: 20px;
`;

const Title = styled.h3`
  font-size: ${({theme})=>theme.font.sizes.h3};
  margin-bottom: 8px;
`;

const Muted = styled.p`
  color: ${({theme})=>theme.textMuted};
`;

const ColorSwatch = styled.div`
  border-radius: ${({theme})=>theme.radius.md}px;
  overflow: hidden;
  border: 1px solid ${({theme})=>theme.border};
  background: ${({theme})=>theme.surface};
`;

const ColorBox = styled.div`
  height: 120px;
  background: ${({hex})=>hex};
`;

const ColorMeta = styled.div`
  padding: 10px 12px;
  display:flex; align-items:center; justify-content:space-between; gap: 10px;
  small { color: ${({theme})=>theme.textMuted}; }
  strong { font-weight:700; }
`;

const Step = styled.div`
  display:flex; gap: 16px; align-items:flex-start;
  padding: 14px; border:1px dashed ${({theme})=>theme.border};
  border-radius:${({theme})=>theme.radius.md}px; background: ${({theme})=>theme.surface};
  counter-increment: step;

  &:before{
    content: counter(step);
    display:inline-flex; align-items:center; justify-content:center;
    width:28px; height:28px; min-width:28px;
    border-radius:${({theme})=>theme.radius.pill}px;
    border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.body};
    font-weight:700;
  }
`;

const Steps = styled.div`
  counter-reset: step;
  display:grid; gap:12px;
`;

const ChipRow = styled.div`
  display:flex; flex-wrap:wrap; gap:8px; margin-top:10px;
  span{
    font-size:13px; padding:6px 10px; border-radius:${({theme})=>theme.radius.pill}px;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
  }
`;

const Gallery = styled.div`
  display:grid; gap:12px;
  grid-template-columns: repeat(3, 1fr);
  @media (max-width: 820px){ grid-template-columns: repeat(2,1fr); }
  @media (max-width: 520px){ grid-template-columns: 1fr; }

  .pic{
    position:relative; border-radius:${({theme})=>theme.radius.lg}px; overflow:hidden;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    height: 220px;
    background-size: cover; background-position:center;
    transition: transform .3s ease;
  }
  .pic:hover{ transform: translateY(-2px); }
`;

const CTA = styled.div`
  display:flex; flex-wrap:wrap; gap:12px; align-items:center; justify-content:space-between;
  padding: 22px;
  border-radius:${({theme})=>theme.radius.lg}px;
  border:1px solid ${({theme})=>theme.border};
  background:
    linear-gradient(0deg, rgba(255,102,0,.10), rgba(255,102,0,.10)),
    ${({theme})=>theme.surface};
  .actions{ display:flex; gap:10px; flex-wrap:wrap; }
`;

const COLORS = [
  { name: 'Polar White',  hex: '#F4F6F8' },
  { name: 'Vanilla',      hex: '#F5E6C8' },
  { name: 'Sand Beige',   hex: '#D8C7B2' },
  { name: 'Light Grey',   hex: '#CBD3D9' },
  { name: 'Graphite',     hex: '#3B3F44' },
  { name: 'Anthracite',   hex: '#202428' },
  { name: 'Ocean',        hex: '#2C5E7E' },
  { name: 'Emerald',      hex: '#0E7A6C' },
  { name: 'Terracotta',   hex: '#C46A3C' },
  { name: 'Lemon',        hex: '#E6D953' },
  { name: 'Walnut',       hex: '#7C5A43' },
  { name: 'Concrete',     hex: '#A0A6AA' },
];

const FEATURES = [
  { t: 'Стійкість до вологи та пари', d: 'HPL не вбирає вологу, підходить для фасадів, санвузлів, лабораторій.' },
  { t: 'Механічна міцність', d: 'Стійкість до подряпин і ударів завдяки пресуванню під високим тиском.' },
  { t: 'Хімічна стійкість', d: 'Не боїться більшості побутових та промислових реагентів.' },
  { t: 'Гігієнічність', d: 'Не підтримує розвиток бактерій, легко миється.' },
  { t: 'Вогнестійкі варіанти', d: 'Доступні плити з підвищеною вогнестійкістю для громадських просторів.' },
  { t: 'Декори та фактури', d: 'Великий вибір однотонів, під дерево, камінь, бетон, металік.' },
];

const MAKE_STEPS = [
  'Відбір декору та товщини (6–12+ мм) відповідно до задачі.',
  'Розкрій плит на форматі з мінімальними втратами.',
  'Обробка крайки (фрезерування, фаска, радіус).',
  'Свердління та підготовка кріплень/технологічних отворів.',
  'Сухе складання та контроль геометрії.',
  'Пакування з захистом поверхні для транспортування.',
];

const INSTALL_STEPS = [
  'Підготовка основи — розмітка та перевірка площин.',
  'Монтаж підсистеми (для вентильованих фасадів) або підкладок.',
  'Свердління HPL під приховане чи видиме кріплення (клепка/гвинт/заклепка).',
  'Компенсаційні зазори та термошви згідно регламентів.',
  'Фінальна підгонка та ущільнення вузлів.',
  'Приймання якості, інструктаж по догляду.',
];

const USE_CASES = [
  'Фасади та вентильовані системи',
  'Інтер’єри торгових центрів та офісів',
  'Лабораторні та медичні меблі',
  'Санвузли, душові, роздягальні',
  'Кухонні стільниці, фартухи',
  'Стіни в громадських просторах (антивандальні рішення)',
];

export default function HplPage() {
  const meta = usePageMeta('hpl');
  useEffect(() => {
    AOS.init({
      duration: 600,
      once: true,
      offset: 80,
      easing: 'ease-out-cubic',
    });
  }, []);

  return (
    <PageWrap>
      <SeoMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        image={meta.image}
        path={meta.path || '/hpl'}
      />
      {/* HERO */}
      <Hero>
        <Container>
          <H1 data-aos="fade-up">HPL-панелі — міцні, вологостійкі, елегантні</H1>
          <Lead data-aos="fade-up" data-aos-delay="80">
            Виготовляємо та постачаємо високоякісні HPL-панелі для фасадів і інтер’єрів.
            Професійний розкрій, обробка крайки, підготовка кріплень і повний супровід монтажу.
          </Lead>
          <Badges data-aos="zoom-in" data-aos-delay="140">
            <span>Фасади</span>
            <span>Інтер’єр</span>
            <span>Лабораторії</span>
            <span>Санвузли</span>
            <span>Антивандальні рішення</span>
          </Badges>
        </Container>
      </Hero>

      {/* Переваги */}
      <Section>
        <Container>
          <h2 data-aos="fade-up">Переваги HPL</h2>
          <Grid className="three" style={{marginTop: 16}}>
            {FEATURES.map((f, i)=>(
              <Card key={i} data-aos="fade-up" data-aos-delay={i*60}>
                <Title>{f.t}</Title>
                <Muted>{f.d}</Muted>
              </Card>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* Калькулятор площі (НОВЕ) */}
      <Section className="surface">
        <Container>
          <h2 data-aos="fade-up">Калькулятор площі HPL</h2>
          <p data-aos="fade-up" data-aos-delay="60" style={{color:'inherit', opacity:.8, marginBottom:12}}>
            Додайте розміри деталей (у мм або м), оберіть запас і формат листа — ми підкажемо орієнтовну кількість.
          </p>
          <AreaCalculator />
        </Container>
      </Section>

      {/* Палiтра кольорiв */}
      <Section>
        <Container>
          <h2 data-aos="fade-up">Приклади колірної палітри</h2>
          <Muted data-aos="fade-up" data-aos-delay="60">
            Нижче — орієнтовна добірка популярних декорів. Під час замовлення ми надамо фактичні зразки виробника.
          </Muted>

          <Grid className="four" style={{marginTop: 16}}>
            {COLORS.map((c, i)=>(
              <ColorSwatch key={c.name} data-aos="zoom-in" data-aos-delay={i%6*60}>
                <ColorBox hex={c.hex} />
                <ColorMeta>
                  <strong>{c.name}</strong>
                  <small>{c.hex.toUpperCase()}</small>
                </ColorMeta>
              </ColorSwatch>
            ))}
          </Grid>
          <ChipRow>
            <span>Мат / Super-Mat</span>
            <span>Текстура «Wood»</span>
            <span>Мікроструктура «Stone»</span>
            <span>Антивідбиткова</span>
            <span>Під замовлення — спецдекори</span>
          </ChipRow>
        </Container>
      </Section>

      {/* Опис продукту */}
      <Section className="surface">
        <Container>
          <Grid className="two">
            <div data-aos="fade-right">
              <h2>Що таке HPL-панелі</h2>
              <Muted>
                HPL (High Pressure Laminate) — багатошаровий ламінат, спресований під високим тиском і температурою.
                Основа — крафт-папір, просочений смолами, та декоративний шар з захисним покриттям. Плити вирізняються
                високою міцністю, стабільністю, гігієнічністю і стійкістю до вологи й хімії.
              </Muted>
              <ChipRow>
                <span>Товщина: 6–12+ мм</span>
                <span>Габарити: до 3050×1300 мм (та ін.)</span>
                <span>Кромка: фаска / R-радіус</span>
                <span>Вогнестійкі версії</span>
              </ChipRow>
            </div>
            <Card data-aos="fade-left">
              <Title>Особливості виготовлення</Title>
              <Muted>
                На власних потужностях виконуємо розкрій, фрезерування, торцювання та свердління. Готуємо кріплення
                під фасадні підсистеми або інтер’єрні рішення, маркуємо деталі та пакуємо для безпечної логістики.
              </Muted>
              <div style={{height:14}} />
              <Title>Монтаж</Title>
              <Muted>
                Працюємо з видимим і прихованим кріпленням, дотримуємося термозазорів та рекомендацій виробника.
                Надаємо інструкції щодо догляду й сервісу.
              </Muted>
            </Card>
          </Grid>
        </Container>
      </Section>

      {/* Етапи виготовлення */}
      <Section>
        <Container>
          <h2 data-aos="fade-up">Етапи виготовлення</h2>
          <Steps style={{marginTop:16}}>
            {MAKE_STEPS.map((s, i)=>(
              <Step key={i} data-aos="fade-up" data-aos-delay={i*60}>
                <div>{s}</div>
              </Step>
            ))}
          </Steps>
        </Container>
      </Section>

      {/* Етапи монтажу */}
      <Section className="surface">
        <Container>
          <h2 data-aos="fade-up">Монтаж</h2>
          <Steps style={{marginTop:16}}>
            {INSTALL_STEPS.map((s, i)=>(
              <Step key={i} data-aos="fade-up" data-aos-delay={i*60}>
                <div>{s}</div>
              </Step>
            ))}
          </Steps>
        </Container>
      </Section>

      {/* Галерея */}
      <Section>
        <Container>
          <h2 data-aos="fade-up">Приклади реалізацій</h2>
          <Gallery style={{marginTop:16}}>
            {[
              'linear-gradient(135deg, #ff6600 0%, #ff9966 100%)',
              'linear-gradient(135deg, #2C5E7E 0%, #86b9d6 100%)',
              'linear-gradient(135deg, #202428 0%, #4a4f55 100%)',
              'linear-gradient(135deg, #0E7A6C 0%, #75c7ba 100%)',
              'linear-gradient(135deg, #C46A3C 0%, #f0b197 100%)',
              'linear-gradient(135deg, #7C5A43 0%, #cbb19f 100%)',
            ].map((bg, i)=>(
              <div
                key={i}
                className="pic"
                data-aos="zoom-in"
                data-aos-delay={(i%3)*80}
                style={{backgroundImage: bg}}
              />
            ))}
          </Gallery>
        </Container>
      </Section>

      {/* Області застосування */}
      <Section className="surface">
        <Container>
          <h2 data-aos="fade-up">Де застосовують</h2>
          <Grid className="three" style={{marginTop:16}}>
            {USE_CASES.map((u, i)=>(
              <Card key={i} data-aos="fade-up" data-aos-delay={i*60}>
                <Title>{u}</Title>
                <Muted>
                  Підберемо товщину, декор, спосіб кріплення та вузли примикань саме під ваш проєкт.
                </Muted>
              </Card>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* FAQ-тизер */}
      <Section>
        <Container>
          <Grid className="two">
            <Card data-aos="fade-right">
              <Title>Питання та відповіді (коротко)</Title>
              <ul style={{margin:0, paddingLeft:18}}>
                <li><strong>Чи підходить HPL для фасадів?</strong> Так, за умови правильної підсистеми і зазорів.</li>
                <li><strong>Як доглядати?</strong> М’яка губка, нейтральні засоби, без абразивів.</li>
                <li><strong>Чи є гарантія?</strong> Так, відповідно до постачальника та виду робіт.</li>
              </ul>
              <div style={{height:12}} />
              <a className="btn btn--ghost" href="/info/faq">Перейти до FAQ →</a>
            </Card>
            <Card data-aos="fade-left">
              <Title>Корисні статті</Title>
              <ul style={{margin:0, paddingLeft:18}}>
                <li><a href="/articles">Вибір товщини HPL для різних задач</a></li>
                <li><a href="/articles">Видиме vs приховане кріплення: що обрати</a></li>
                <li><a href="/articles">Догляд за HPL у громадських просторах</a></li>
              </ul>
              <div style={{height:12}} />
              <a className="btn btn--ghost" href="/articles">Усі статті →</a>
            </Card>
          </Grid>
        </Container>
      </Section>

      {/* CTA */}
      <Section className="surface">
        <Container>
          <CTA data-aos="zoom-in">
            <div>
              <h3 style={{marginBottom:6}}>Потрібен прорахунок HPL-рішення?</h3>
              <Muted>Надішліть креслення/габарити — підберемо декори, підсистему та розкрій під ваш бюджет.</Muted>
            </div>
            <div className="actions">
              <a className="btn btn--primary" href="/contacts">Зв’язатися з нами</a>
              <a className="btn btn--ghost" href="/info/docs">Документи / специфікації</a>
            </div>
          </CTA>
        </Container>
      </Section>
    </PageWrap>
  );
}
